import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import {
  Language as LanguageIcon,
  Refresh as RefreshIcon,
  CloudDownload as ImportIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const FastlyServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importDialog, setImportDialog] = useState({ open: false, service: null });
  const [importing, setImporting] = useState(false);
  const [purgeTypes, setPurgeTypes] = useState(['url', 'surrogate_key']);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [withWww, setWithWww] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { isAdmin } = useAuth();

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    if (isAdmin) {
      fetchFastlyServices();
    }
  }, [isAdmin]);

  const fetchFastlyServices = async () => {
    try {
      setLoading(true);
      const result = await api.fastly.getServices();
      setServices(result.services);
    } catch (error) {
      // Failed to fetch Fastly services
      toast.error('Failed to load Fastly services. Please configure API token.');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceDomains = async (serviceId) => {
    try {
      setLoadingDomains(true);
      const result = await api.fastly.getServiceDomains(serviceId);
      setDomains(result.domains || []);
      
      // Auto-select first domain if available
      if (result.domains && result.domains.length > 0) {
        const firstDomain = result.domains[0].apexDomain;
        setSelectedDomain(firstDomain);
        setWithWww(false);
        
        // Auto-fill site name with the first domain
        setValue('name', firstDomain);
      }
    } catch (error) {
      // Failed to fetch service domains
      toast.error('Failed to load domains for this service.');
      setDomains([]);
    } finally {
      setLoadingDomains(false);
    }
  };

  const handleImportService = async (service) => {
    setImportDialog({ open: true, service });
    reset({
      name: service.name,
      description: service.comment || '',
    });
    setPurgeTypes(['url', 'surrogate_key']);
    setSelectedDomain('');
    setWithWww(false);
    setDomains([]);
    
    // Fetch domains for this service
    await fetchServiceDomains(service.id);
  };

  const onSubmitImport = async (data) => {
    if (!selectedDomain) {
      toast.error('Please select a domain');
      return;
    }

    // Determine final domain based on apex domain and www checkbox
    const finalDomain = withWww ? `www.${selectedDomain}` : selectedDomain;

    setImporting(true);
    try {
      const siteData = {
        ...data,
        domain: finalDomain,
        service_id: importDialog.service.id,
        fastly_token: 'SERVER_MANAGED', // Special flag to use server-side token
        purge_types: purgeTypes,
      };

      await api.sites.create(siteData);
      toast.success(`Service "${importDialog.service.name}" imported successfully!`);
      setImportDialog({ open: false, service: null });
      
      // Trigger a refresh of sites if we're in a context that has sites
      window.dispatchEvent(new CustomEvent('siteAdded'));
    } catch (error) {
      toast.error(error.message || 'Failed to import service');
    } finally {
      setImporting(false);
    }
  };

  const handlePurgeTypeChange = (type, checked) => {
    if (checked) {
      setPurgeTypes(prev => [...prev, type]);
    } else {
      setPurgeTypes(prev => prev.filter(t => t !== type));
    }
  };

  // Filter services based on search term (search in service name and domains)
  const filteredServices = services.filter(service => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in service name
    if (service.name.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Search in domains
    if (service.domains && service.domains.length > 0) {
      return service.domains.some(domain => 
        domain.toLowerCase().includes(searchLower)
      );
    }
    
    return false;
  });

  if (!isAdmin) {
    return (
      <Alert severity="info">
        Only administrators can view and import Fastly services.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          Available Fastly Services
        </Typography>

        <Box display="flex" gap={2} alignItems="center">
          <TextField
            label="Search services or domains"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="e.g., bettingtop10.bg"
            sx={{ minWidth: 250 }}
          />
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchFastlyServices}
            variant="outlined"
          >
            Refresh Services
          </Button>
        </Box>
      </Box>

      {services.length === 0 ? (
        <Alert severity="info">
          No Fastly services found. Please check your API token configuration.
        </Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            {searchTerm ? (
              <>
                Found {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''} matching "{searchTerm}" 
                {filteredServices.length !== services.length && ` (${services.length} total services)`}
              </>
            ) : (
              <>Found {services.length} Fastly services. Click "Import" to add them to your cache purger.</>
            )}
          </Alert>

          {filteredServices.length === 0 && searchTerm ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              No services found matching "{searchTerm}". Try searching for service names or domain names.
            </Alert>
          ) : null}
          
          <Grid container spacing={3}>
            {filteredServices.map((service) => (
              <Grid item xs={12} md={6} lg={4} key={service.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <LanguageIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" component="h2" fontWeight={600}>
                        {service.name}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Service ID:</strong> {service.id}
                    </Typography>
                    
                    {service.comment && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Description:</strong> {service.comment}
                      </Typography>
                    )}
                    
                    {service.domains && service.domains.length > 0 && (
                      <Box mt={1} mb={2}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Domains:</strong>
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {service.domains.map((domain, index) => {
                            const isHighlighted = searchTerm && domain.toLowerCase().includes(searchTerm.toLowerCase());
                            return (
                              <Chip
                                key={index}
                                label={domain}
                                size="small"
                                color={isHighlighted ? "secondary" : "primary"}
                                variant={isHighlighted ? "filled" : "outlined"}
                                sx={{ fontSize: '0.75rem' }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Versions:</strong> {service.versions?.length || 0}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                      Created: {new Date(service.created_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Button
                      startIcon={<ImportIcon />}
                      onClick={() => handleImportService(service)}
                      variant="contained"
                      size="small"
                    >
                      Import Service
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Import Dialog */}
      <Dialog
        open={importDialog.open}
        onClose={() => setImportDialog({ open: false, service: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Import Service: {importDialog.service?.name}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(onSubmitImport)} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Site Name"
                  {...register('name', { required: 'Site name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={domains}
                  getOptionLabel={(option) => option.apexDomain}
                  value={domains.find(d => d.apexDomain === selectedDomain) || null}
                  onChange={(event, newValue) => {
                    const domain = newValue ? newValue.apexDomain : '';
                    setSelectedDomain(domain);
                    setWithWww(false); // Reset www option when changing domain
                    
                    // Auto-fill site name with the selected domain
                    if (domain) {
                      setValue('name', domain);
                    }
                  }}
                  disabled={loadingDomains}
                  loading={loadingDomains}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Apex Domain *"
                      required
                      error={!selectedDomain && domains.length > 0}
                      helperText={
                        domains.length === 0 && !loadingDomains
                          ? "Please ensure this service has domains configured in Fastly"
                          : !selectedDomain && domains.length > 0
                          ? "Please select a domain"
                          : `${domains.length} domain${domains.length !== 1 ? 's' : ''} available`
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDomains ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">{option.apexDomain}</Typography>
                        {option.comment && (
                          <Typography variant="caption" color="text.secondary">
                            {option.comment}
                          </Typography>
                        )}
                        <Box sx={{ mt: 0.5 }}>
                          {option.hasWww && (
                            <Chip label="WWW" size="small" variant="outlined" sx={{ mr: 0.5, fontSize: '0.7rem', height: '18px' }} />
                          )}
                          {option.hasWildcard && (
                            <Chip label="Wildcard" size="small" variant="outlined" sx={{ mr: 0.5, fontSize: '0.7rem', height: '18px' }} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  noOptionsText={loadingDomains ? "Loading domains..." : "No domains found for this service"}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px', // Limit height and make scrollable
                    },
                  }}
                />
              </Grid>

              {/* WWW Option */}
              {selectedDomain && (
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Domain Configuration</FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={withWww}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setWithWww(isChecked);
                              
                              // Update site name based on www option
                              if (selectedDomain) {
                                const siteName = isChecked ? `www.${selectedDomain}` : selectedDomain;
                                setValue('name', siteName);
                              }
                            }}
                            disabled={!selectedDomain || !(domains.find(d => d.apexDomain === selectedDomain)?.hasWww || domains.find(d => d.apexDomain === selectedDomain)?.hasWildcard)}
                          />
                        }
                        label={
                          <Box>
                            <Typography component="span">Include WWW subdomain</Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Use www.{selectedDomain} instead of {selectedDomain}
                              {!domains.find(d => d.apexDomain === selectedDomain)?.hasWww && 
                               !domains.find(d => d.apexDomain === selectedDomain)?.hasWildcard && 
                               " (Not available - no www or wildcard domain found)"}
                            </Typography>
                          </Box>
                        }
                      />
                    </FormGroup>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Final domain:</strong> {withWww ? `www.${selectedDomain}` : selectedDomain}
                    </Typography>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  {...register('description')}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Allowed Purge Types</FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={purgeTypes.includes('url')}
                          onChange={(e) => handlePurgeTypeChange('url', e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography component="span">URL Specific Purging</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Purge individual pages or files by exact URL
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={purgeTypes.includes('surrogate_key')}
                          onChange={(e) => handlePurgeTypeChange('surrogate_key', e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography component="span">Surrogate Key Purging</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Purge all pages tagged with a specific key (e.g., domain name)
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={purgeTypes.includes('purge_all')}
                          onChange={(e) => handlePurgeTypeChange('purge_all', e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography component="span">Purge All Cache</Typography>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ color: 'warning.main' }}>
                            ⚠️ Purges ALL content for ALL domains in the Fastly service
                          </Typography>
                        </Box>
                      }
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog({ open: false, service: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmitImport)}
            variant="contained"
            disabled={importing}
            startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
          >
            {importing ? 'Importing...' : 'Import Service'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FastlyServices;
